/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { math } from 'polished';
import { BorderLeftRadius, BorderRadius, BorderRightRadius } from './mixins/_border-radius.mixin';
import { FontSize } from './vendor/_rfs.mixin';

export default $ => {
  const rules = [];

  //
  // Base styles
  //
  rules.push(css`
    .input-group {
      position: relative;
      display: flex;
      flex-wrap: wrap; // For form validation feedback
      align-items: stretch;
      width: 100%;

      > .form-control,
      > .form-control-plaintext,
      > .custom-select,
      > .custom-file {
        position: relative; // For focus state's z-index
        flex: 1 1 auto;
        width: 1%;
        min-width: 0; // https://stackoverflow.com/questions/36247140/why-dont-flex-items-shrink-past-content-size
        margin-bottom: 0;

        + .form-control,
        + .custom-select,
        + .custom-file {
          margin-left: ${math(`-${$.inputBorderWidth}`)};
        }
      }

      // Bring the "active" form control to the top of surrounding elements
      > .form-control:focus,
      > .custom-select:focus,
      > .custom-file .custom-file-input:focus ~ .custom-file-label {
        z-index: 3;
      }

      // Bring the custom file input above the label
      > .custom-file .custom-file-input:focus {
        z-index: 4;
      }

      > .form-control,
      > .custom-select {
        &:not(:first-of-type) {
          ${new BorderLeftRadius(0).include($)};
        }
      }

      // Custom file inputs have more complex markup, thus requiring different
      // border-radius overrides.
      > .custom-file {
        display: flex;
        align-items: center;

        &:not(:last-child) .custom-file-label,
        &:not(:last-child) .custom-file-label::after {
          ${new BorderRightRadius(0).include($)};
        }
        &:not(:first-of-type) .custom-file-label {
          ${new BorderLeftRadius(0).include($)};
        }
      }

      &:not(.has-validation) {
        > .form-control:not(:last-child),
        > .custom-select:not(:last-child),
        > .custom-file:not(:last-child) .custom-file-label,
        > .custom-file:not(:last-child) .custom-file-label::after {
          ${new BorderRightRadius(0).include($)};
        }
      }

      &.has-validation {
        > .form-control:nth-last-of-type(n + 3),
        > .custom-select:nth-last-of-type(n + 3),
        > .custom-file:nth-last-of-type(n + 3) .custom-file-label,
        > .custom-file:nth-last-of-type(n + 3) .custom-file-label::after {
          ${new BorderRightRadius(0).include($)};
        }
      }
    }
  `);

  // Prepend and append
  //
  // While it requires one extra layer of HTML for each, dedicated prepend and
  // append elements allow us to 1) be less clever, 2) simplify our selectors, and
  // 3) support HTML5 form validation.
  rules.push(css`
    .input-group-prepend,
    .input-group-append {
      display: flex;

      // Ensure buttons are always above inputs for more visually pleasing borders.
      // This isn't needed for \`.input-group-text\` since it shares the same border-color
      // as our inputs.
      .btn {
        position: relative;
        z-index: 2;
        transition: background-color 0.3s ease, transform 0.2s ease;

        &:focus {
          z-index: 3;
        }
      }

      .btn:hover {
        transform: scale(1.03);
      }

      .btn + .btn,
      .btn + .input-group-text,
      .input-group-text + .input-group-text,
      .input-group-text + .btn {
        margin-left: ${math(`-${$.inputBorderWidth}`)};
      }
    }
  `);

  rules.push(css`
    .input-group-prepend {
      margin-right: ${math(`-${$.inputBorderWidth}`)};
      ${new BorderLeftRadius($.inputBorderRadius).include($)};
    }
    .input-group-append {
      margin-left: ${math(`-${$.inputBorderWidth}`)};
      ${new BorderRightRadius($.inputBorderRadius).include($)};
    }
  `);

  // Textual addons
  //
  // Serves as a catch-all element for any text or radio/checkbox input you wish
  // to prepend or append to an input.
  rules.push(css`
    .input-group-text {
      display: flex;
      align-items: center;
      padding: ${$.inputPaddingY} ${$.inputPaddingX};
      margin-bottom: 0; // Allow use of <label> elements by overriding our default margin-bottom
      ${new FontSize($.inputFontSize).include($)}; // Match inputs
      font-weight: ${$.fontWeightNormal};
      line-height: ${$.inputLineHeight};
      color: ${$.inputGroupAddonColor};
      text-align: center;
      white-space: nowrap;
      background-color: ${$.inputGroupAddonBg};
      border: ${$.inputBorderWidth} solid ${$.inputGroupAddonBorderColor};

      // Nuke default margins from checkboxes and radios to vertically center within.
      input[type='radio'],
      input[type='checkbox'] {
        margin-top: 0;
      }
    }
  `);

  // Sizing
  //
  // Remix the default form control sizing classes into new ones for easier
  // manipulation.
  rules.push(css`
    .input-group-lg > .form-control:not(textarea),
    .input-group-lg > .custom-select {
      height: ${$.inputHeightLg};
    }
  `);

  rules.push(css`
    .input-group-lg > .form-control,
    .input-group-lg > .custom-select,
    .input-group-lg > .input-group-prepend > .input-group-text,
    .input-group-lg > .input-group-append > .input-group-text,
    .input-group-lg > .input-group-prepend > .btn,
    .input-group-lg > .input-group-append > .btn {
      padding: ${$.inputPaddingYLg} ${$.inputPaddingXLg};
      ${new FontSize($.inputFontSizeLg).include($)};
      line-height: ${$.inputLineHeightLg};
      ${new BorderRadius($.inputBorderRadiusLg).include($)};
    }
  `);

  rules.push(css`
    .input-group-sm > .form-control:not(textarea),
    .input-group-sm > .custom-select {
      height: ${$.inputHeightSm};
    }
  `);

  rules.push(css`
    .input-group-sm > .form-control,
    .input-group-sm > .custom-select,
    .input-group-sm > .input-group-prepend > .input-group-text,
    .input-group-sm > .input-group-append > .input-group-text,
    .input-group-sm > .input-group-prepend > .btn,
    .input-group-sm > .input-group-append > .btn {
      padding: ${$.inputPaddingYSm} ${$.inputPaddingXSm};
      ${new FontSize($.inputFontSizeSm).include($)};
      line-height: ${$.inputLineHeightSm};
      ${new BorderRadius($.inputBorderRadiusSm).include($)};
    }
  `);

  rules.push(css`
    .input-group-lg > .custom-select,
    .input-group-sm > .custom-select {
      padding-right: ${math(`${$.customSelectPaddingX} + ${$.customSelectIndicatorPadding}`)};
    }
  `);

  // Prepend and append rounded corners
  //
  // These rulesets must come after the sizing ones to properly override sm and lg
  // border-radius values when extending. They're more specific than we'd like
  // with the `.input-group >` part, but without it, we cannot override the sizing.
  rules.push(css`
    .input-group > .input-group-prepend > .btn,
    .input-group > .input-group-prepend > .input-group-text,
    .input-group:not(.has-validation) > .input-group-append:not(:last-child) > .btn,
    .input-group:not(.has-validation) > .input-group-append:not(:last-child) > .input-group-text,
    .input-group.has-validation > .input-group-append:nth-last-of-type(n + 3) > .btn,
    .input-group.has-validation > .input-group-append:nth-last-of-type(n + 3) > .input-group-text,
    .input-group > .input-group-append:last-child > .btn:not(:last-child):not(.dropdown-toggle),
    .input-group > .input-group-append:last-child > .input-group-text:not(:last-child) {
      ${new BorderRightRadius(0).include($)};
    }
  `);

  rules.push(css`
    .input-group > .input-group-append > .btn,
    .input-group > .input-group-append > .input-group-text,
    .input-group > .input-group-prepend:not(:first-of-type) > .btn,
    .input-group > .input-group-prepend:not(:first-of-type) > .input-group-text,
    .input-group > .input-group-prepend:first-of-type > .btn:not(:first-of-type),
    .input-group > .input-group-prepend:first-of-type > .input-group-text:not(:first-of-type) {
      ${new BorderLeftRadius(0).include($)};
    }
  `);

  return css(rules);
};
